import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandS, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';

import { Shape } from '../Shape';
import { useWheelReward } from '../utils';
import type { Slot_probabilisticBundleSlotConfig } from './__generated__/index.graphql';

const StyledShape = styled(Shape)`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Content = styled(Vertical).attrs({
  center: true,
})`
  width: 100%;
  padding: var(--double-unit) var(--unit);
`;
const Corner = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

type LayoutProps = {
  percent: number;
  big?: boolean;
  illustration?: string | React.ReactNode;
  description?: React.ReactNode;
};

const Layout = ({ percent, big, illustration, description }: LayoutProps) => {
  const Text = big ? BrandL : BrandS;
  return (
    <Content
      style={{
        width: big ? '200px' : undefined,
        height: big ? '317px' : undefined,
      }}
    >
      {big ? null : (
        <Corner>
          <LabelS color="var(--c-nd-500)">
            <FormattedNumber
              value={percent}
              style="percent"
              maximumFractionDigits={2}
            />
          </LabelS>
        </Corner>
      )}
      {typeof illustration === 'string' ? (
        <ResponsiveImg
          src={illustration}
          alt=""
          width={big ? 120 : 60}
          cropWidth={big ? 160 : 80}
        />
      ) : (
        illustration
      )}
      <Text className="text-center">{description}</Text>
    </Content>
  );
};

type SlotProps = {
  config: Slot_probabilisticBundleSlotConfig;
  big?: boolean;
  style?: React.CSSProperties;
  rarity: Rarity;
};
export const Slot = ({ config, big, style, rarity }: SlotProps) => {
  const { illustration, description } = useWheelReward(config);
  return (
    <StyledShape config={config} style={style} rarity={rarity}>
      <Layout
        percent={config.probability}
        big={big}
        illustration={illustration}
        description={description}
      />
    </StyledShape>
  );
};

Slot.fragments = {
  ProbabilisticBundleSlotConfig: gql`
    fragment Slot_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      probability
      ...Shape_probabilisticBundleSlotConfig
      ...useWheelReward_probabilisticBundleSlotConfig
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}
    ${useWheelReward.fragments.probabilisticBundleSlotConfig}
  ` as TypedDocumentNode<Slot_probabilisticBundleSlotConfig>,
};
